import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { Building2, Network, Pencil, Plus, Trash2, UserCheck } from 'lucide-react'
import { AppShell, Crumb, Flag as CountryFlag, SkeletonCards, Surface, tokens } from '@agribot/polygon-checks-ui'
import {
  useAddParent, useEditParent, useParentCompanies, useRenameSupplier, useReviewers, useRoles, useSetParent, useSetReviewers, useStaff, useSuppliers,
  type DeletionTarget, type ParentCompany, type Person, type Supplier,
} from '../api'
import { DeleteDialog } from '../components/DeleteDialog'
import { countryName } from '@agribot/sourcing-model'
import { useStructure } from '../hooks'
import { DiagramDialog } from '../components/TreeDialog'
import { GroupStructure } from '../components/GroupStructure'

const NONE = ''

/**
 * Who the buyer's suppliers are, as the campaign sees them: legal entities
 * grouped under parent companies (so one origin serves the whole group), and
 * who reviews each one: the commercial buyer at level 1, the regional
 * sustainable-sourcing lead at level 2. Their review queues follow from this.
 * Changing it takes the campaign admin role.
 */
export function Suppliers() {
  const { data: parents, isPending: loadingParents, error } = useParentCompanies()
  const { data: suppliers, isPending: loadingSuppliers } = useSuppliers()
  const canEdit = useRoles().includes('admin')
  const setParent = useSetParent()
  const [adding, setAdding] = useState(false)
  const [reviewing, setReviewing] = useState<Supplier | null>(null)
  const [structureOf, setStructureOf] = useState<ParentCompany | null>(null)
  const [editingParent, setEditingParent] = useState<ParentCompany | null>(null)
  const [renaming, setRenaming] = useState<Supplier | null>(null)
  const [deleting, setDeleting] = useState<DeletionTarget | null>(null)
  const label = (p: ParentCompany) => p.path?.length ? p.path.join(' › ') : p.name

  const parentOf = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of parents ?? []) for (const s of p.suppliers) m.set(s.id, p.id)
    return m
  }, [parents])
  const groups = [
    ...(parents ?? []).map((p) => ({ id: p.id, name: label(p), ref: p.externalRef, depth: p.depth ?? 0, company: p, rows: (suppliers ?? []).filter((s) => parentOf.get(s.id) === p.id) })),
    { id: NONE, name: 'Not in a parent company', ref: null, depth: 0, company: null, rows: (suppliers ?? []).filter((s) => !parentOf.has(s.id)) },
  ].filter((g) => g.rows.length || g.id !== NONE)

  return (
    <AppShell
      crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Suppliers</Crumb>]}
      actions={canEdit ? <Button startIcon={<Plus size={16} />} onClick={() => setAdding(true)}>Add parent company</Button> : undefined}
    >
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>Suppliers</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6, maxWidth: 720 }}>
          Legal entities grouped under their parent company: an origin one entity declares, the others in its group can use.
          Each supplier’s reviewers decide whose queue its submission lands in.
          {!canEdit && ' Changing this takes the campaign admin role.'}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 4 }}>{(error as Error).message}</Alert>}
        {setParent.isError && <Alert severity="error" sx={{ mb: 4 }}>{(setParent.error as Error).message}</Alert>}
        {(loadingParents || loadingSuppliers) && <SkeletonCards columns="1fr" />}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {groups.map((g) => (
            <Surface key={g.id || 'none'} sx={{ ml: g.depth * 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, px: 5, py: 3.5, borderBottom: `1px solid ${tokens.color.ink200}` }}>
                <Building2 size={18} strokeWidth={1.5} color={g.id ? tokens.color.brandDeep : tokens.color.ink400} />
                <Typography variant="h4" component="h2" sx={{ flex: 1, color: g.id ? 'text.primary' : 'text.secondary' }}>{g.name}</Typography>
                {g.ref && <Typography sx={{ font: `400 12px ${tokens.font.mono}`, color: 'text.secondary' }}>{g.ref}</Typography>}
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{g.rows.length} legal entit{g.rows.length === 1 ? 'y' : 'ies'}</Typography>
                {g.company && !g.company.parentId && (
                  <Button size="small" variant="text" startIcon={<Network size={14} />} onClick={() => setStructureOf(g.company)}>Group structure</Button>
                )}
                {g.company && canEdit && (
                  <>
                    <Tooltip title="Rename, or move under another company">
                      <IconButton aria-label={`Edit ${g.company.name}`} onClick={() => setEditingParent(g.company)} sx={{ color: tokens.color.ink500 }}>
                        <Pencil size={15} strokeWidth={1.75} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete parent company">
                      <IconButton aria-label={`Delete ${g.company.name}`} onClick={() => setDeleting({ kind: 'parent', id: g.company!.id })} sx={{ color: tokens.color.ink500, '&:hover': { color: tokens.color.danger } }}>
                        <Trash2 size={15} strokeWidth={1.75} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
              {g.rows.length === 0 ? (
                <Typography variant="body2" sx={{ px: 5, py: 4, color: 'text.secondary' }}>No legal entities yet. Move a supplier in below.</Typography>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Legal entity</TableCell>
                      <TableCell sx={{ width: 280 }}>Parent company</TableCell>
                      <TableCell sx={{ width: 200 }}>Reviewers</TableCell>
                      {canEdit && <TableCell sx={{ width: 104 }}><Box component="span" sx={{ position: 'absolute', left: -9999 }}>Actions</Box></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.rows.map((s) => (
                      <TableRow key={s.id} sx={{ '& td': { height: 56 } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CountryFlag code={s.country_code} size={12} />
                            <Box sx={{ color: 'text.primary', fontWeight: 500 }}>{s.name}</Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={parentOf.get(s.id) ?? NONE}
                            disabled={!canEdit || setParent.isPending}
                            onChange={(e) => setParent.mutate({ supplierId: s.id, parentId: e.target.value || null })}
                            inputProps={{ 'aria-label': `Parent company of ${s.name}` }}
                            SelectProps={{ displayEmpty: true }}
                            sx={{ width: 250 }}
                          >
                            <MenuItem value={NONE}>None</MenuItem>
                            {(parents ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{label(p)}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <ReviewerSummary supplier={s} onEdit={canEdit ? () => setReviewing(s) : undefined} />
                        </TableCell>
                        {canEdit && (
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Rename">
                              <IconButton aria-label={`Rename ${s.name}`} onClick={() => setRenaming(s)} sx={{ color: tokens.color.ink500 }}>
                                <Pencil size={15} strokeWidth={1.75} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete supplier">
                              <IconButton aria-label={`Delete ${s.name}`} onClick={() => setDeleting({ kind: 'supplier', id: s.id })} sx={{ color: tokens.color.ink500, '&:hover': { color: tokens.color.danger } }}>
                                <Trash2 size={15} strokeWidth={1.75} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Surface>
          ))}
        </Box>
      </Box>
      {adding && <AddParentDialog parents={parents ?? []} onClose={() => setAdding(false)} />}
      {structureOf && <StructureDialog company={structureOf} onClose={() => setStructureOf(null)} />}
      {reviewing && <ReviewersDialog supplier={reviewing} onClose={() => setReviewing(null)} />}
      {editingParent && <EditParentDialog company={editingParent} parents={parents ?? []} onClose={() => setEditingParent(null)} />}
      {renaming && <RenameSupplierDialog supplier={renaming} onClose={() => setRenaming(null)} />}
      {deleting && <DeleteDialog target={deleting} onClose={() => setDeleting(null)} />}
    </AppShell>
  )
}

function ReviewerSummary({ supplier, onEdit }: { supplier: Supplier; onEdit?: () => void }) {
  const { data } = useReviewers(supplier.id)
  const n1 = data?.level1.length ?? 0
  const n2 = data?.level2.length ?? 0
  const missing = data && (!n1 || !n2)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ font: `400 13px ${tokens.font.sans}`, color: missing ? tokens.color.warning : 'text.primary' }}>
        {data ? (missing ? `${!n1 ? 'No buyer' : ''}${!n1 && !n2 ? ', ' : ''}${!n2 ? 'no regional lead' : ''}` : `${n1} + ${n2}`) : '…'}
      </Box>
      {onEdit && <Button size="small" variant="text" startIcon={<UserCheck size={14} />} onClick={onEdit}>Set</Button>}
    </Box>
  )
}

function StructureDialog({ company, onClose }: { company: ParentCompany; onClose: () => void }) {
  const { data, error, isPending } = useStructure(company.id)
  return (
    <DiagramDialog title={`${company.name}: companies, legal entities, materials and origins`} fileName={`${company.name}-structure`} onClose={onClose}>
      {(ref) => (
        <>
          {isPending && <Box sx={{ p: 6 }}><SkeletonCards columns="1fr" /></Box>}
          {error && <Alert severity="error" sx={{ m: 4 }}>{(error as Error).message}</Alert>}
          {data && <GroupStructure ref={ref} structure={data} countryName={(code) => countryName(code, 'en')} />}
        </>
      )}
    </DiagramDialog>
  )
}

function AddParentDialog({ parents, onClose }: { parents: ParentCompany[]; onClose: () => void }) {
  const add = useAddParent()
  const [name, setName] = useState('')
  const [ref, setRef] = useState('')
  const [within, setWithin] = useState(NONE)
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); add.mutate({ name: name.trim(), externalRef: ref.trim() || null, parentId: within || null }, { onSuccess: onClose }) }}>
        <DialogTitle>Add parent company</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: '8px !important' }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus InputLabelProps={{ shrink: true }} />
          <TextField select label="Part of" value={within} onChange={(e) => setWithin(e.target.value)} helperText="For a company inside a larger group. Origins are shared across the whole top-level group." InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value={NONE}>Nothing: a top-level group</MenuItem>
            {parents.map((p) => <MenuItem key={p.id} value={p.id}>{p.path?.length ? p.path.join(' › ') : p.name}</MenuItem>)}
          </TextField>
          <TextField label="Your reference (optional)" helperText="E.g. the group's id in SAP, so a sync can find it." value={ref} onChange={(e) => setRef(e.target.value)} InputLabelProps={{ shrink: true }} />
          {add.isError && <Alert severity="error">{(add.error as Error).message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || add.isPending}>Add</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

function ReviewersDialog({ supplier, onClose }: { supplier: Supplier; onClose: () => void }) {
  const { data: staff = [] } = useStaff()
  const { data: current } = useReviewers(supplier.id)
  const save = useSetReviewers(supplier.id)
  const [level1, setLevel1] = useState<Person[]>([])
  const [level2, setLevel2] = useState<Person[]>([])
  useEffect(() => {
    if (current) { setLevel1(current.level1); setLevel2(current.level2) }
  }, [current])
  const label = (p: Person) => p.fullName ?? p.email ?? p.id.slice(0, 8)

  const picker = (title: string, help: string, role: 'buyer' | 'regional_lead', value: Person[], onChange: (v: Person[]) => void) => (
    <Autocomplete
      multiple
      options={staff}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, p) => (
        <Box component="li" {...props} key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <span>{label(p)}</span>
          {!p.roles?.includes(role) && <Typography variant="caption" sx={{ color: tokens.color.warning }}>no {role.replace('_', ' ')} role</Typography>}
        </Box>
      )}
      renderTags={(v, getTagProps) => v.map((p, i) => <Chip {...getTagProps({ index: i })} key={p.id} label={label(p)} size="small" />)}
      renderInput={(params) => <TextField {...params} label={title} helperText={help} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
    />
  )

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Who reviews {supplier.name}?</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 5, pt: '8px !important' }}>
        {picker('First review: commercial buyer', 'Sees the submission first and sends it on or back.', 'buyer', level1, setLevel1)}
        {picker('Final review: regional sustainable-sourcing lead', 'Approves it, or returns it to the supplier.', 'regional_lead', level2, setLevel2)}
        {save.isError && <Alert severity="error">{(save.error as Error).message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
        <Button variant="text" onClick={onClose}>Cancel</Button>
        <Button disabled={save.isPending} onClick={() => save.mutate({ level1: level1.map((p) => p.id), level2: level2.map((p) => p.id) }, { onSuccess: onClose })}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Every company under this one, at any depth: none of them can become its parent. */
function below(id: string, parents: ParentCompany[]): Set<string> {
  const out = new Set<string>([id])
  let grew = true
  while (grew) {
    grew = false
    for (const p of parents) if (p.parentId && out.has(p.parentId) && !out.has(p.id)) { out.add(p.id); grew = true }
  }
  return out
}

function EditParentDialog({ company, parents, onClose }: { company: ParentCompany; parents: ParentCompany[]; onClose: () => void }) {
  const edit = useEditParent()
  const [name, setName] = useState(company.name)
  const [ref, setRef] = useState(company.externalRef ?? '')
  const [within, setWithin] = useState(company.parentId ?? NONE)
  const excluded = below(company.id, parents)
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={(e) => {
        e.preventDefault()
        edit.mutate({ id: company.id, name: name.trim(), externalRef: ref.trim() || null, parentId: within || null }, { onSuccess: onClose })
      }}>
        <DialogTitle>Edit {company.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: '8px !important' }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus InputLabelProps={{ shrink: true }} />
          <TextField select label="Part of" value={within} onChange={(e) => setWithin(e.target.value)}
            helperText="Move it under another company, or make it a top-level group. Its legal entities and origins move with it."
            InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value={NONE}>Nothing: a top-level group</MenuItem>
            {parents.filter((p) => !excluded.has(p.id)).map((p) => <MenuItem key={p.id} value={p.id}>{p.path?.length ? p.path.join(' › ') : p.name}</MenuItem>)}
          </TextField>
          <TextField label="Your reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} InputLabelProps={{ shrink: true }} />
          {edit.isError && <Alert severity="error">{(edit.error as Error).message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || edit.isPending}>Save</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

function RenameSupplierDialog({ supplier, onClose }: { supplier: Supplier; onClose: () => void }) {
  const rename = useRenameSupplier()
  const [name, setName] = useState(supplier.name)
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); rename.mutate({ id: supplier.id, name: name.trim() }, { onSuccess: onClose }) }}>
        <DialogTitle>Rename {supplier.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: '8px !important' }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus InputLabelProps={{ shrink: true }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>To move it to another parent company, use its Parent company menu.</Typography>
          {rename.isError && <Alert severity="error">{(rename.error as Error).message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || name.trim() === supplier.name || rename.isPending}>Save</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
